import type {
  AdjustBalanceResponse,
  AdminAccountsSort,
  BillingAnomalyResolvedFilter,
  GetAccountDetailResponse,
  ListBillingAnomaliesQuery,
  ListBillingAnomaliesResponse,
  ListAccountsQuery,
  ListAccountsResponse,
  MarkEmailVerifiedResponse,
  ResendEmailVerificationResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  type AdjustBalanceClientRequest,
  type MarkEmailVerifiedClientRequest,
  type ResendEmailVerificationClientRequest,
  type SoftDeleteAccountResult,
  type SuspendAccountClientRequest,
  type SuspendAccountResult,
  type UnsuspendAccountResult,
  adjustBalance,
  getAccount,
  markEmailVerified,
  listBillingAnomalies,
  listAccounts,
  resendEmailVerification,
  softDeleteAccount,
  suspendAccount,
  unsuspendAccount,
} from './admin.ts';
import { CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY } from './customerManagerWorkspace.ts';

/**
 * React Query wrappers for the `/v1/ops/admin/*` proxy.
 *
 * Query key conventions:
 *   - `['admin', 'accounts', query]`                 — account list scoped by
 *     the current search / pagination / filter tuple.
 *   - `['admin', 'accounts', accountId]`             — single-account detail.
 *
 * Mutation invalidation map:
 *   - useAdjustBalance         → invalidate the root `['admin','accounts']`
 *     subtree (list + all scoped account details) so the adjusted row's
 *     balance + recent transactions refresh on navigation.
 */

export const ADMIN_NAMESPACE = ['admin'] as const;
export const ACCOUNTS_ROOT_KEY = ['admin', 'accounts'] as const;
export const BILLING_ANOMALIES_ROOT_KEY = ['admin', 'billing-anomalies'] as const;

export interface AccountsListScope {
  readonly limit: number;
  readonly offset: number;
  readonly email: string;
  readonly hasBalance: boolean;
  /**
   * Point-in-time effective-balance (balance + own credit headroom) <= 0
   * filter (#1013; credit-aware since epic #1033 E4-C3 #1127) — same
   * definition as the funnel's `low_balance_accounts` count, NOT the cockpit's
   * burn-based chase ranking. Classification is server-side; a credit customer
   * with headroom is excluded.
   */
  readonly lowBalance: boolean;
  /** `'balance'` = effective balance ascending; `'created'` = newest-first default. */
  readonly sort: AdminAccountsSort;
}

export function accountsListQueryKey(
  scope: AccountsListScope,
): readonly ['admin', 'accounts', AccountsListScope] {
  return ['admin', 'accounts', scope] as const;
}

export function accountQueryKey(
  accountId: string,
): readonly ['admin', 'accounts', string] {
  return ['admin', 'accounts', accountId] as const;
}

export function billingAnomaliesQueryKey(
  filter: BillingAnomalyResolvedFilter,
): readonly ['admin', 'billing-anomalies', BillingAnomalyResolvedFilter] {
  return ['admin', 'billing-anomalies', filter] as const;
}

export function useAccountsList(
  scope: AccountsListScope,
): UseQueryResult<ListAccountsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: accountsListQueryKey(scope),
    queryFn: ({ signal }) => {
      const query: ListAccountsQuery = {
        limit: scope.limit,
        offset: scope.offset,
      };
      const trimmedEmail = scope.email.trim();
      if (trimmedEmail.length > 0) {
        query.email = trimmedEmail;
      }
      if (scope.hasBalance) {
        query.hasBalance = 'true';
      }
      if (scope.lowBalance) {
        query.lowBalance = 'true';
      }
      if (scope.sort === 'balance') {
        query.sort = 'balance';
      }
      return listAccounts(query, fetchImpl, signal);
    },
  });
}

export function useAccount(
  accountId: string | undefined,
): UseQueryResult<GetAccountDetailResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: accountQueryKey(accountId ?? ''),
    queryFn: ({ signal }) => {
      if (accountId === undefined || accountId.length === 0) {
        throw new Error('useAccount: accountId is required');
      }
      return getAccount(accountId, fetchImpl, signal);
    },
    enabled: accountId !== undefined && accountId.length > 0,
  });
}

export interface AdjustBalanceVars {
  readonly accountId: string;
  readonly body: AdjustBalanceClientRequest;
}

export function useAdjustBalance(): UseMutationResult<
  AdjustBalanceResponse,
  Error,
  AdjustBalanceVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => adjustBalance(accountId, body, fetchImpl),
    onSuccess: () => {
      // Invalidating the root `['admin','accounts']` key hits every scoped
      // list variant (different search / pagination tuples) AND every
      // single-account detail in one shot. Simpler and safer than trying
      // to rebuild the exact scope+id keys from the mutation variables.
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      // The Customer 360 page (#861 PR A) embeds the same account payload
      // under the customer-manager workspace namespace; invalidate it too
      // so admin actions taken from 360 refresh in place. No-op when the
      // namespace has no mounted queries (e.g. on AccountDetail).
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export interface ResendEmailVerificationVars {
  readonly accountId: string;
  readonly body: ResendEmailVerificationClientRequest;
}

export function useResendEmailVerification(): UseMutationResult<
  ResendEmailVerificationResponse,
  Error,
  ResendEmailVerificationVars
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: ({ accountId, body }) => resendEmailVerification(accountId, body, fetchImpl),
  });
}

export interface MarkEmailVerifiedVars {
  readonly accountId: string;
  readonly body: MarkEmailVerifiedClientRequest;
}

export function useMarkEmailVerified(): UseMutationResult<
  MarkEmailVerifiedResponse,
  Error,
  MarkEmailVerifiedVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => markEmailVerified(accountId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      // Refresh the embedded account payload on the Customer 360 page too
      // (#861 PR A). See useAdjustBalance for rationale.
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export function useBillingAnomaliesList(
  filter: BillingAnomalyResolvedFilter,
  options: { readonly limit?: number; readonly offset?: number } = {},
): UseQueryResult<ListBillingAnomaliesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: billingAnomaliesQueryKey(filter),
    queryFn: ({ signal }) => {
      const query: ListBillingAnomaliesQuery = {
        resolvedFilter: filter,
      };
      if (options.limit !== undefined) {
        query.limit = options.limit;
      }
      if (options.offset !== undefined) {
        query.offset = options.offset;
      }
      return listBillingAnomalies(query, fetchImpl, signal);
    },
  });
}

// ─── Account lifecycle (Epic #109 / P2.10) ───────────────────────────────

export interface SuspendAccountVars {
  readonly accountId: string;
  readonly body: SuspendAccountClientRequest;
}

export function useSuspendAccount(): UseMutationResult<
  SuspendAccountResult,
  Error,
  SuspendAccountVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, body }) => suspendAccount(accountId, body, fetchImpl),
    onSuccess: () => {
      // Same broad-invalidation as useAdjustBalance — the account detail
      // card re-reads from the backend, which now reflects the fresh
      // suspended_at timestamp + disables the Suspend button.
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export function useUnsuspendAccount(): UseMutationResult<
  UnsuspendAccountResult,
  Error,
  { readonly accountId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }) => unsuspendAccount(accountId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}

export function useSoftDeleteAccount(): UseMutationResult<
  SoftDeleteAccountResult,
  Error,
  { readonly accountId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId }) => softDeleteAccount(accountId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_ROOT_KEY });
      void qc.invalidateQueries({ queryKey: CUSTOMER_MANAGER_WORKSPACE_ROOT_KEY });
    },
  });
}
